import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { BulkSearchService } from '../../../services/bulk-search.service';
import { GlobalLoaderService } from '../../global-loader/global-loader.service';
import { GenericCommanService } from '../../../services/generic-common.service';
import { ProjectHistoryService } from '../../../services/project-history.service';
import { TaskProgressWebSocketService, WsProgressMessage } from '../../../services/task-progress-websocket.service';
import { merge, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CompositeScoreWidgetComponent } from '../../components/composite-score-widget/composite-score-widget.component';
import { AreaChartComponent } from '../../charts/area-chart/area-chart.component';
import { CommonCircleLoaderComponent } from '../../components/common-circle-loader/common-circle-loader.component';
// import { CitationCardComponent } from '../../components/citation-card/citation-card.component';
import { TaskStatus } from '../../models/bulk-search.models';

@Component({
  selector: 'app-generic-ai-audit',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    CompositeScoreWidgetComponent,
    AreaChartComponent,
    CommonCircleLoaderComponent,
    // CitationCardComponent
  ],
  templateUrl: './generic-ai-audit.component.html',
  styleUrl: './generic-ai-audit.component.scss'
})
export class GenericAIAuditComponent implements OnInit, OnDestroy {
  auditResult: any = null;
  auditError: string | null = null;
  loading: boolean = false;
  private subscriptions: Subscription[] = [];

  // Properties for display
  compositeScore: number = 0;
  auditAreas: { [key: string]: { current: number; potential: number; total_questions: number; passed_questions: number; questions: any[] } } = {};
  questionsData: Array<{
    category: string;
    question: string;
    pass: number;
    score?: number;
    answer?: string;
    options?: string;
    evidence: string;
  }> = [];
  
  // Chart data
  areaChartData: any[] = []; // Format: [{name: "Current/Potential", series: [{name: "Category", value: score}]}]

  // Evidence sources
  evidenceSources: any[] = [];

  // High-level AI audit metrics (for summary cards)
  auditMetrics: {
    ai_readiness?: { score: number; description: string };
    static_content?: { score: number; description: string };
    page_speed?: { score: number; description: string };
    structured_data?: { score: number; description: string };
  } | null = null;

  // Multi-page audit data
  isMultiPageAudit: boolean = false;
  baseDomain: string = '';
  discoveredPages: string[] = [];
  discoveredPagesMetrics: { [url: string]: any } = {}; // Metrics for each discovered page
  discoveryPagesNA: boolean = false; // Flag for NA when no site URL found
  discoveryPagesFormatOnly: boolean = false; // Flag for format-only (no HTML)
  pageResults: { [url: string]: any } = {};
  summaryMetrics: {
    robots_txt?: { status: string; description: string; blocked_crawlers?: string[] };
    llms_txt?: { status: string; description: string; file_exists?: boolean };
    ai_readiness?: { score: number; description: string };
    static_content?: { score: number; description: string };
    page_speed?: { score: number; description: string };
    structured_data?: { score: number; description: string };
  } | null = null;
  expandedPages: Set<string> = new Set();
  expandedCategories: Set<string> = new Set(); // Track expanded question categories
  
  // Both results storage - separate properties for each view
  singlePageResult: any = null;
  multiPageResult: any = null;
  
  // Single-page view properties
  singlePageBaseDomain: string = '';
  singlePageDiscoveredPages: string[] = [];
  singlePageResults: { [url: string]: any } = {};
  singlePageSummaryMetrics: any = null;
  singlePageCompositeScore: number = 0;
  singlePageAuditAreas: { [key: string]: { current: number; potential: number; total_questions: number; passed_questions: number; questions: any[] } } = {};
  singlePageQuestionsData: Array<{
    category: string;
    question: string;
    pass: number;
    score?: number;
    answer?: string;
    options?: string;
    evidence: string;
  }> = [];
  singlePageAuditMetrics: any = null;
  singlePageAreaChartData: any[] = [];
  
  // Multi-page view properties
  multiPageBaseDomain: string = '';
  multiPageDiscoveredPages: string[] = [];
  // discoveredPagesMetrics is defined in main properties section above (line 66)
  summaryDiscoveredPagesMetrics: any = null; // robots.txt/llms.txt from multi-page
  multiPageResults: { [url: string]: any } = {};
  multiPageSummaryMetrics: any = null;
  multiPageCompositeScore: number = 0;
  multiPageAuditAreas: { [key: string]: { current: number; potential: number; total_questions: number; passed_questions: number; questions: any[] } } = {};
  multiPageQuestionsData: Array<{
    category: string;
    question: string;
    pass: number;
    score?: number;
    answer?: string;
    options?: string;
    evidence: string;
  }> = [];
  multiPageAuditMetrics: any = null;
  multiPageAreaChartData: any[] = [];

  private snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private bulkSearchService = inject(BulkSearchService);
  private globalLoaderService = inject(GlobalLoaderService);
  private genericCommanService = inject(GenericCommanService);
  private projectHistoryService = inject(ProjectHistoryService);
  reAuditLoading = false;
  private projectId: string | null = null;
  private statusPollingInterval: any = null;
  private taskProgressWs = inject(TaskProgressWebSocketService);
  private wsSubscription: Subscription | undefined;
  private analysisWsSub: Subscription | undefined;
  private aiAuditTerminalHandled = false;
  
  constructor(
    private genericTemplateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Get projectId from route params
    this.route.params.subscribe(params => {
      this.projectId = params['projectId'] || null;
    });
    
    // Check if we already have AI Audit data from history or previous analysis
    const existingData = this.genericTemplateCommonService.getCurrentAIAuditResponse();
    if (existingData) {
      this.auditResult = existingData;
      this.processAIAuditData(existingData);
      this.loading = false;
      this.cdr.detectChanges();
    } else {
      // If no existing data, try to load from ProjectData history API (only on page load)
      const urlParams = new URLSearchParams(window.location.search);
      const projectdataId = urlParams.get('projectdataId');
      if (projectdataId) {
        this.loadAuditDataFromHistory(projectdataId);
      }
    }

    // Subscribe to AI Audit response from service
    const aiAuditSub = this.genericTemplateCommonService.aiAuditResponse$.subscribe((response: any) => {
      if (response) {
        this.auditResult = response;
        this.processAIAuditData(response);
        this.loading = false;
        this.auditError = null;
        this.cdr.detectChanges();
      }
    });

    // Subscribe to loading state
    // Only show loading if we don't have data yet
    const loadingSub = this.genericTemplateCommonService.isLoading$.subscribe(loading => {
      // Only set loading to true if we don't have audit result yet
      // Once we have data, ignore the global loading state
      if (!this.auditResult) {
        this.loading = loading;
      } else {
        // If we have data, ensure loading is false
        this.loading = false;
      }
    });

    // Subscribe to error state
    const errorSub = this.genericTemplateCommonService.error$.subscribe(error => {
      if (error) {
        this.auditError = error;
      }
    });

    // Add subscriptions to array
    this.subscriptions.push(aiAuditSub, loadingSub, errorSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.wsSubscription?.unsubscribe();
    this.analysisWsSub?.unsubscribe();
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
    }
  }

  private stopStatusPolling(): void {
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
      this.statusPollingInterval = null;
    }
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = undefined;
    this.analysisWsSub?.unsubscribe();
    this.analysisWsSub = undefined;
  }

  /**
   * Start task-based audit with status polling
   */
  private startTaskBasedAudit(request: { project_id?: string; projectdata_id?: string; filters?: any }): void {
    this.aiAuditTerminalHandled = false;
    this.reAuditLoading = true;
    this.loading = true;
    this.auditError = null;
    
    // Initialize progress
    this.globalLoaderService.initializeProgress(1);
    this.globalLoaderService.updateProgress(0, 0, [], 'Starting audit...', null, 1);
    
    // Start audit task
    this.genericCommanService.processAiAudit(request).subscribe({
      next: (response: any) => {
        const taskId = response.task_id;
        if (!taskId) {
          this.auditError = 'Failed to start audit: No task ID returned';
          this.reAuditLoading = false;
          this.loading = false;
          return;
        }
        
        // Start polling status
        this.startStatusPolling(taskId);
      },
      error: (error: any) => {
        console.error('Error starting audit:', error);
        this.auditError = error.error?.detail || 'Failed to start audit';
        this.reAuditLoading = false;
        this.loading = false;
        this.globalLoaderService.resetLoader();
      }
    });
  }

  /**
   * Poll audit task status (or subscribe to WebSocket when environment.useWebSocket).
   */
  private startStatusPolling(taskId: string): void {
    const applyStatus = (status: any) => {
      this.globalLoaderService.updateProgress(
        status.progress_percentage || 0,
        0,
        [],
        status.current_step || 'Processing...',
        status.estimated_time_remaining,
        1
      );
    };
    const onCompleted = (status: any) => {
      if (status.result) {
        this.auditResult = status.result;
        this.processAIAuditData(status.result);
        this.loading = false;
        this.reAuditLoading = false;
        this.globalLoaderService.resetLoader();
        this.genericTemplateCommonService.updateAIAuditResponse(status.result);
        this.snackBar.open('AI audit completed! Results are shown below.', 'Close', { duration: 5000 });
      } else {
        this.auditError = 'Audit completed but no result available';
        this.loading = false;
        this.reAuditLoading = false;
        this.globalLoaderService.resetLoader();
      }
      this.cdr.detectChanges();
    };
    const onFailed = (status: any) => {
      this.auditError = status.error_message || 'Audit failed';
      this.loading = false;
      this.reAuditLoading = false;
      this.globalLoaderService.resetLoader();
      this.cdr.detectChanges();
    };

    if (this.taskProgressWs.useWebSocket) {
      this.stopStatusPolling();
      this.taskProgressWs.connect();
      const { projectId: pid, projectdataId: pdid } = this.genericTemplateCommonService.getCurrentProjectContext();
      const auditStreams: Observable<WsProgressMessage>[] = [this.taskProgressWs.messagesByPath('audit')];
      if (pid && pdid) {
        auditStreams.unshift(this.taskProgressWs.messagesByPath(`${pid}_${pdid}`));
      }
      this.wsSubscription = merge(...auditStreams)
        .pipe(filter((msg) => (msg.data as any)?.task_id === taskId))
        .subscribe((msg) => {
          const status = msg.data as any;
          if (!status) return;
          applyStatus(status);
          if (status.status === 'completed') {
            if (this.aiAuditTerminalHandled) return;
            this.aiAuditTerminalHandled = true;
            this.stopStatusPolling();
            onCompleted(status);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            if (this.aiAuditTerminalHandled) return;
            this.aiAuditTerminalHandled = true;
            this.stopStatusPolling();
            if (status.status === 'cancelled') {
              this.auditError = 'Audit was cancelled';
              this.loading = false;
              this.reAuditLoading = false;
              this.globalLoaderService.resetLoader();
              this.cdr.detectChanges();
            } else {
              onFailed(status);
            }
          }
          this.cdr.detectChanges();
        });

      this.analysisWsSub = this.taskProgressWs
        .messagesByPath('analysis')
        .pipe(
          filter((msg) => {
            const d = msg.data as Record<string, unknown> | undefined;
            return (
              d?.['task_id'] === taskId &&
              ['completed', 'failed', 'cancelled'].includes(String(d?.['status'] || ''))
            );
          })
        )
        .subscribe((msg) => {
          if (this.aiAuditTerminalHandled) return;
          const d = msg.data as Record<string, unknown>;
          const st = String(d['status'] || '');
          this.aiAuditTerminalHandled = true;
          this.stopStatusPolling();
          if (st === 'completed') {
            this.globalLoaderService.resetLoader();
            this.reAuditLoading = false;
            this.loading = false;
            const pd =
              pdid ||
              this.genericTemplateCommonService.getCurrentProjectContext().projectdataId ||
              new URLSearchParams(window.location.search).get('projectdataId');
            if (pd) {
              this.loadAuditDataFromHistory(pd);
              this.snackBar.open('AI audit completed! Refreshing results…', 'Close', { duration: 5000 });
            } else {
              this.snackBar.open('AI audit completed.', 'Close', { duration: 5000 });
            }
          } else if (st === 'cancelled') {
            this.auditError = 'Audit was cancelled';
            this.loading = false;
            this.reAuditLoading = false;
            this.globalLoaderService.resetLoader();
          } else {
            onFailed({ error_message: 'Audit failed' });
          }
          this.cdr.detectChanges();
        });
      return;
    }

    let pollCount = 0;
    const initialInterval = 2000;
    const regularInterval = 5000;
    const pollStatus = () => {
      this.genericCommanService.getAuditStatus(taskId).subscribe({
        next: (status: any) => {
          pollCount++;
          applyStatus(status);
          if (status.status === 'completed') {
            if (this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = null;
            }
            onCompleted(status);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            if (this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = null;
            }
            if (status.status === 'cancelled') {
              this.auditError = 'Audit was cancelled';
              this.loading = false;
              this.reAuditLoading = false;
              this.globalLoaderService.resetLoader();
            } else {
              onFailed(status);
            }
          } else {
            if (pollCount === 3 && this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = setInterval(pollStatus, regularInterval);
            }
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error polling status:', error);
          if (this.statusPollingInterval) {
            clearInterval(this.statusPollingInterval);
            this.statusPollingInterval = null;
          }
          this.auditError = 'Failed to get audit status';
          this.loading = false;
          this.reAuditLoading = false;
          this.globalLoaderService.resetLoader();
          this.cdr.detectChanges();
        }
      });
    };
    this.statusPollingInterval = setInterval(pollStatus, initialInterval);
    pollStatus();
  }

  /**
   * Process AI Audit data and populate component properties
   * Processes BOTH single-page and multi-page results separately
   */
  processAIAuditData(data: any): void {
    if (!data) {
      console.warn('processAIAuditData: No data provided');
      return;
    }

    console.log('🔍 processAIAuditData: Processing data', data);
    console.log('🔍 processAIAuditData: data keys', Object.keys(data || {}));

    // Extract data from structured_data if present (from bulk search)
    // Handle nested structure: data["ai-audit"]["structured_data"] or data.structured_data or data
    let actualData = data.structured_data;
    
    // Check if data has "ai-audit" key (nested structure)
    if (!actualData && data['ai-audit']) {
      actualData = data['ai-audit'].structured_data || data['ai-audit'];
      console.log('🔍 processAIAuditData: Found nested ai-audit structure');
    }
    
    // Fallback to data itself
    if (!actualData) {
      actualData = data;
    }

    // Extract evidence sources from Tavily
    this.evidenceSources = this.transformSourcesData(
      actualData?.evidence_sources || 
      data.evidence_sources || 
      []
    );
    
    console.log('🔍 processAIAuditData: Extracted actualData', actualData);
    console.log('🔍 processAIAuditData: actualData keys', Object.keys(actualData || {}));
    console.log('🔍 processAIAuditData: Evidence sources', this.evidenceSources.length);

    // Check for NEW brand/pages format (Step 5 update)
    // New format has: brand { url, composite_score, audit_areas, questions_data, area_chart_data, summary }
    //                 pages { discovered_pages, pages_metrics, summary, discovery_pages_na, discovery_pages_format_only }
    const isNewBrandPagesFormat = actualData.brand && actualData.pages;
    
    if (isNewBrandPagesFormat) {
      console.log('✅ Detected new brand/pages format - processing');
      this._processBrandPagesFormat(actualData);
      return;
    }

    // Check for OLD flattened format (backward compatibility)
    // Old format has: base_domain, composite_score, results, audit_areas, questions_data, etc. at root level
    const isOldFlattenedFormat = actualData.base_domain && 
        actualData.composite_score !== undefined &&
        actualData.audit_areas &&
        !actualData.single_page_audit &&
        !actualData.single_page_result && 
        !actualData.multi_page_result &&
        !actualData.brand &&
        !actualData.pages;
    
    if (isOldFlattenedFormat) {
      console.log('⚠️ Detected old flattened format - processing (backward compatibility)');
      this._processFlattenedResult(actualData);
      return;
    }
    
    // Check for OLD format with single_page_audit wrapper (backward compatibility)
    const isOldUnifiedFormat = actualData.base_domain && 
        actualData.composite_score !== undefined &&
        actualData.single_page_audit &&
        !actualData.single_page_result && 
        !actualData.multi_page_result;
    
    if (isOldUnifiedFormat) {
      console.log('⚠️ Detected old format with single_page_audit wrapper - processing');
      // Extract data from single_page_audit and process as flattened
      const flattenedData = {
        ...actualData,
        ...actualData.single_page_audit
      };
      // Remove single_page_audit wrapper
      delete flattenedData.single_page_audit;
      this._processFlattenedResult(flattenedData);
      return;
    }
    
    // Check for OLD unified format (no single_page_result/multi_page_result, but has results directly)
    // Old unified format has: base_domain, results, summary_metrics, discovered_pages, composite_score
    const isOldDirectFormat = actualData.base_domain && 
        actualData.results && 
        Object.keys(actualData.results || {}).length > 0 &&
        actualData.summary_metrics && 
        !actualData.single_page_result && 
        !actualData.multi_page_result &&
        !actualData.single_page_audit;
    
    if (isOldDirectFormat) {
      console.log('✅ Detected old direct format - processing unified result');
      this._processFlattenedResult(actualData);
      return;
    }
    
    // Check for OLD format with separate single_page_result/multi_page_result (backward compatibility)
    const singlePageResult = actualData.single_page_result;
    const multiPageResult = actualData.multi_page_result;
    
    if (singlePageResult || multiPageResult) {
      console.log('⚠️ Detected old format with separate results - processing separately');
      // Store both results
      this.singlePageResult = singlePageResult;
      this.multiPageResult = multiPageResult;
      
      // Process BOTH results separately
      if (singlePageResult) {
        this._processSinglePageResult(singlePageResult);
      }
      
      if (multiPageResult) {
        this._processMultiPageResult(multiPageResult);
      }
      
      // Set primary display to multi-page if available, else single-page
      if (multiPageResult) {
        this._setPrimaryDisplay(multiPageResult);
      } else if (singlePageResult) {
        this._setPrimaryDisplay(singlePageResult);
      }
      return;
    }
    
    // Fallback: check if it's old format (direct structure)
    if (actualData.base_domain && actualData.discovered_pages && actualData.results) {
      // Old multi-page format - process as multi-page
      this._processMultiPageResult(actualData);
    } else if (actualData.composite_score !== undefined) {
      // Old single-page format - process as single-page
      this._processOldSinglePageFormat(actualData);
    } else {
      console.warn('❌ No audit results found');
    }
  }
  
  /**
   * Process single-page result separately
   */
  private _processSinglePageResult(result: any): void {
    this.singlePageBaseDomain = result.base_domain || '';
    this.singlePageDiscoveredPages = result.discovered_pages || [];
    this.singlePageResults = result.results || {};
    this.singlePageSummaryMetrics = result.summary_metrics || null;
    
    // Use summary metrics for the cards
    if (this.singlePageSummaryMetrics) {
      this.singlePageAuditMetrics = {
        ai_readiness: this.singlePageSummaryMetrics.ai_readiness,
        static_content: this.singlePageSummaryMetrics.static_content,
        page_speed: this.singlePageSummaryMetrics.page_speed,
        structured_data: this.singlePageSummaryMetrics.structured_data
      };
    }
    
    // Calculate composite score
    const scores = Object.values(this.singlePageResults)
      .map((r: any) => r.composite_score || 0)
      .filter((s: number) => s > 0);
    this.singlePageCompositeScore = scores.length > 0
      ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
      : 0;
    
    // Get audit areas from first page
    const firstPageResult = Object.values(this.singlePageResults)[0];
    if (firstPageResult && firstPageResult.audit_areas) {
      this.singlePageAuditAreas = {};
      
      Object.keys(firstPageResult.audit_areas).forEach(category => {
        const areaData = firstPageResult.audit_areas[category];
        this.singlePageAuditAreas[category] = {
          current: areaData.current_score || 0,
          potential: areaData.potential_score || 100,
          total_questions: areaData.total_questions || 0,
          passed_questions: areaData.passed_questions || 0,
          questions: areaData.questions || []
        };
      });

      // Collect all questions
      this.singlePageQuestionsData = [];
      Object.keys(firstPageResult.audit_areas).forEach(category => {
        const areaData = firstPageResult.audit_areas[category];
        if (areaData.questions && Array.isArray(areaData.questions)) {
          areaData.questions.forEach((q: any) => {
            this.singlePageQuestionsData.push({
              category: category,
              question: q.question || '',
              pass: q.pass || 0,
              score: q.score,
              answer: q.answer,
              options: q.options,
              evidence: q.evidence || ''
            });
          });
        }
      });
    }
    
    // Update chart data for single-page
    this._updateSinglePageAreaChart();
  }
  
  /**
   * Process multi-page result separately
   */
  private _processMultiPageResult(result: any): void {
    this.multiPageBaseDomain = result.base_domain || '';
    this.multiPageDiscoveredPages = result.discovered_pages || [];
    this.multiPageResults = result.results || {};
    this.multiPageSummaryMetrics = result.summary_metrics || null;
    
    // Use summary metrics for the cards
    if (this.multiPageSummaryMetrics) {
      this.multiPageAuditMetrics = {
        ai_readiness: this.multiPageSummaryMetrics.ai_readiness,
        static_content: this.multiPageSummaryMetrics.static_content,
        page_speed: this.multiPageSummaryMetrics.page_speed,
        structured_data: this.multiPageSummaryMetrics.structured_data
      };
    }
    
    // Calculate composite score
    const scores = Object.values(this.multiPageResults)
      .map((r: any) => r.composite_score || 0)
      .filter((s: number) => s > 0);
    this.multiPageCompositeScore = scores.length > 0
      ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
      : 0;
      
    // Get audit areas from first page
    const firstPageResult = Object.values(this.multiPageResults)[0];
    if (firstPageResult && firstPageResult.audit_areas) {
      this.multiPageAuditAreas = {};
      
      Object.keys(firstPageResult.audit_areas).forEach(category => {
        const areaData = firstPageResult.audit_areas[category];
        this.multiPageAuditAreas[category] = {
          current: areaData.current_score || 0,
          potential: areaData.potential_score || 100,
          total_questions: areaData.total_questions || 0,
          passed_questions: areaData.passed_questions || 0,
          questions: areaData.questions || []
        };
      });

      // Collect all questions
      this.multiPageQuestionsData = [];
      Object.keys(firstPageResult.audit_areas).forEach(category => {
        const areaData = firstPageResult.audit_areas[category];
        if (areaData.questions && Array.isArray(areaData.questions)) {
          areaData.questions.forEach((q: any) => {
            this.multiPageQuestionsData.push({
              category: category,
              question: q.question || '',
              pass: q.pass || 0,
              score: q.score,
              answer: q.answer,
              options: q.options,
              evidence: q.evidence || ''
            });
          });
        }
      });
    }
    
    // Update chart data for multi-page
    this._updateMultiPageAreaChart();
  }
  
  /**
   * Set primary display (for backward compatibility)
   */
  private _setPrimaryDisplay(result: any): void {
    this.baseDomain = result.base_domain || '';
    this.discoveredPages = result.discovered_pages || [];
    this.pageResults = result.results || {};
    this.summaryMetrics = result.summary_metrics || null;
    this.isMultiPageAudit = (result.discovered_pages || []).length > 1;
    
      if (this.summaryMetrics) {
        this.auditMetrics = {
          ai_readiness: this.summaryMetrics.ai_readiness,
          static_content: this.summaryMetrics.static_content,
          page_speed: this.summaryMetrics.page_speed,
          structured_data: this.summaryMetrics.structured_data
        };
      }
      
      const scores = Object.values(this.pageResults)
        .map((r: any) => r.composite_score || 0)
        .filter((s: number) => s > 0);
      this.compositeScore = scores.length > 0
        ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
        : 0;
      
      const firstPageResult = Object.values(this.pageResults)[0];
      if (firstPageResult && firstPageResult.audit_areas) {
      this.auditAreas = {};
      Object.keys(firstPageResult.audit_areas).forEach(category => {
        const areaData = firstPageResult.audit_areas[category];
        this.auditAreas[category] = {
          current: areaData.current_score || 0,
          potential: areaData.potential_score || 100,
          total_questions: areaData.total_questions || 0,
          passed_questions: areaData.passed_questions || 0,
          questions: areaData.questions || []
        };
      });
      
      this.questionsData = [];
      Object.keys(firstPageResult.audit_areas).forEach(category => {
        const areaData = firstPageResult.audit_areas[category];
        if (areaData.questions && Array.isArray(areaData.questions)) {
          areaData.questions.forEach((q: any) => {
            this.questionsData.push({
              category: category,
              question: q.question || '',
              pass: q.pass || 0,
              score: q.score,
              answer: q.answer,
              options: q.options,
              evidence: q.evidence || ''
            });
          });
        }
      });
    }
    
    this.updateAreaChart();
  }
  
  /**
   * Update area chart for single-page
   */
  private _updateSinglePageAreaChart(): void {
    const categories = Object.keys(this.singlePageAuditAreas);
    
    const currentSeries = categories.map(category => ({
      name: category,
      value: this.singlePageAuditAreas[category].current
    }));

    const potentialSeries = categories.map(category => ({
      name: category,
      value: this.singlePageAuditAreas[category].potential
    }));

    this.singlePageAreaChartData = [
      {
        name: 'Current',
        series: currentSeries
      },
      {
        name: 'Potential',
        series: potentialSeries
      }
    ];
  }
  
  /**
   * Update area chart for multi-page
   */
  private _updateMultiPageAreaChart(): void {
    const categories = Object.keys(this.multiPageAuditAreas);
    
    const currentSeries = categories.map(category => ({
      name: category,
      value: this.multiPageAuditAreas[category].current
    }));

    const potentialSeries = categories.map(category => ({
      name: category,
      value: this.multiPageAuditAreas[category].potential
    }));

    this.multiPageAreaChartData = [
      {
        name: 'Current',
        series: currentSeries
      },
      {
        name: 'Potential',
        series: potentialSeries
      }
    ];
  }
  
  /**
   * Process new brand/pages format (Step 5 update)
   * Structure: { brand: {...}, pages: {...} }
   */
  private _processBrandPagesFormat(data: any): void {
    console.log('✅ Processing new brand/pages format');
    
    // Extract brand data (single brand audit)
    const brandData = data.brand || {};
    this.compositeScore = brandData.composite_score || 0;
    this.baseDomain = brandData.url || '';
    
    // Extract audit areas from brand
    this.auditAreas = {};
    if (brandData.audit_areas) {
      Object.keys(brandData.audit_areas).forEach(category => {
        const areaData = brandData.audit_areas[category];
        this.auditAreas[category] = {
          current: areaData.current_score || 0,
          potential: areaData.potential_score || 100,
          total_questions: areaData.total_questions || 0,
          passed_questions: areaData.passed_questions || 0,
          questions: areaData.questions || []
        };
      });
    }
    
    // Extract questions data from brand
    this.questionsData = brandData.questions_data || [];
    
    // Extract area chart data from brand
    this.areaChartData = brandData.area_chart_data || [];
    
    // Extract pages data (multiple pages audit)
    const pagesData = data.pages || {};
    this.discoveredPages = pagesData.discovered_pages || [];
    this.discoveredPagesMetrics = pagesData.pages_metrics || {}; // Renamed from discovered_pages_metrics
    this.discoveryPagesNA = pagesData.discovery_pages_na || false; // Store as component property
    this.discoveryPagesFormatOnly = pagesData.discovery_pages_format_only || false; // Store as component property
    
    // Also set multiPageDiscoveredPages for backward compatibility with getter methods
    this.multiPageDiscoveredPages = this.discoveredPages;
    
    // Set multi-page flag if pages are available
    this.isMultiPageAudit = this.discoveredPages.length > 0 && !this.discoveryPagesNA;
    
    console.log('✅ Brand/pages format processed:', {
      brandScore: this.compositeScore,
      brandAreas: Object.keys(this.auditAreas).length,
      questionsCount: this.questionsData.length,
      discoveredPages: this.discoveredPages.length,
      pagesMetrics: Object.keys(this.discoveredPagesMetrics).length,
      isMultiPage: this.isMultiPageAudit
    });
  }
  
  /**
   * Process flattened result structure (old format - backward compatibility)
   */
  private _processFlattenedResult(result: any): void {
    console.log('🔧 _processFlattenedResult: Processing flattened result', result);
    
    if (!result) {
      console.error('❌ _processFlattenedResult: No result provided');
      return;
    }
    
    // Extract base URL result (only audited page) from results
    let baseDomain = result.base_domain || '';
    console.log('🔧 _processFlattenedResult: baseDomain', baseDomain);
    
    const results = result.results || {};
    console.log('🔧 _processFlattenedResult: results keys', Object.keys(results));
    
    // Try multiple ways to get baseResult - handle domain variations
    let baseResult = results[baseDomain];
    
    // Try with different domain formats
    if (!baseResult || Object.keys(baseResult).length === 0) {
      // Try exact match with www
      baseResult = results[`www.${baseDomain.replace(/^www\./, '')}`];
    }
    
    if (!baseResult || Object.keys(baseResult).length === 0) {
      // Try without www
      const domainWithoutWww = baseDomain.replace(/^www\./, '');
      baseResult = results[domainWithoutWww];
    }
    
    if (!baseResult || Object.keys(baseResult).length === 0) {
      // Try with https://
      baseResult = results[`https://${baseDomain}`] || results[`https://www.${baseDomain.replace(/^www\./, '')}`];
    }
    
    if (!baseResult || Object.keys(baseResult).length === 0) {
      // Try finding by domain match (any key containing the domain)
      const domainKey = Object.keys(results).find(key => {
        const keyDomain = key.replace(/^https?:\/\//, '').replace(/^www\./, '');
        const baseDomainClean = baseDomain.replace(/^www\./, '');
        return keyDomain === baseDomainClean || key === baseDomain || key.includes(baseDomain) || baseDomain.includes(keyDomain);
      });
      if (domainKey) {
        baseResult = results[domainKey];
        console.log('🔧 _processFlattenedResult: Found matching key', domainKey);
      }
    }
    
    // Fallback to first result
    if (!baseResult || Object.keys(baseResult).length === 0) {
      baseResult = Object.values(results)[0] as any;
      console.log('🔧 _processFlattenedResult: Using first result as fallback');
    }
    
    console.log('🔧 _processFlattenedResult: baseResult', baseResult);
    console.log('🔧 _processFlattenedResult: baseResult keys', baseResult ? Object.keys(baseResult) : 'null');
    
    if (!baseResult || Object.keys(baseResult).length === 0) {
      console.error('❌ _processFlattenedResult: No base result found');
      console.error('❌ Available keys in results:', Object.keys(results));
      return;
    }
    
    // Set properties (all data is at root level now)
    this.baseDomain = baseDomain;
    this.discoveredPages = result.discovered_pages || [];
    this.discoveredPagesMetrics = result.discovered_pages_metrics || {}; // Metrics for each discovered page
    this.discoveryPagesNA = result.discovery_pages_na || false; // Store as component property
    this.discoveryPagesFormatOnly = result.discovery_pages_format_only || false; // Store as component property
    this.pageResults = { [baseDomain]: baseResult };
    this.summaryMetrics = result.summary_metrics || null;
    console.log('🔧 _processFlattenedResult: summaryMetrics', this.summaryMetrics);
    
    // Get composite score
    let compositeScore = result.composite_score || baseResult.composite_score || 0;
    console.log('🔧 _processFlattenedResult: compositeScore (initial)', compositeScore);
    
    this.compositeScore = compositeScore;
    this.isMultiPageAudit = false; // Only base URL is audited
    
    // Extract audit areas from result (at root level now)
    const auditAreas = result.audit_areas || baseResult.audit_areas || {};
    console.log('🔧 _processFlattenedResult: auditAreas', auditAreas);
    console.log('🔧 _processFlattenedResult: auditAreas keys', Object.keys(auditAreas));
    
    if (auditAreas && Object.keys(auditAreas).length > 0) {
      this.auditAreas = {};
      const scores: number[] = [];
      
      Object.keys(auditAreas).forEach(category => {
        const areaData = auditAreas[category];
        const currentScore = areaData.current_score || areaData.current || 0;
        const potentialScore = areaData.potential_score || areaData.potential || 100;
        
        this.auditAreas[category] = {
          current: currentScore,
          potential: potentialScore,
          total_questions: areaData.total_questions || 0,
          passed_questions: areaData.passed_questions || 0,
          questions: areaData.questions || []
        };
        
        // Collect scores for composite calculation
        if (currentScore > 0) {
          scores.push(currentScore);
        }
        
        console.log(`🔧 _processFlattenedResult: Set ${category} current=${currentScore}, potential=${potentialScore}`);
      });
      
      // Calculate composite score from audit areas if it's still 0
      if (this.compositeScore === 0 && scores.length > 0) {
        this.compositeScore = Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10;
        console.log('🔧 _processFlattenedResult: Calculated compositeScore from audit areas', this.compositeScore);
      }
      
      console.log('🔧 _processFlattenedResult: Final auditAreas', this.auditAreas);
      
      // Collect all questions from questions_data (at root level)
      this.questionsData = (result.questions_data || []).map((q: any) => ({
        category: q.category || '',
        question: q.question || '',
        pass: q.pass || 0,
        score: q.score,
        answer: q.answer,
        options: q.options,
        evidence: q.evidence || ''
      }));
      if (this.questionsData.length === 0) {
        Object.keys(auditAreas).forEach(category => {
          const areaData = auditAreas[category];
          if (areaData.questions && Array.isArray(areaData.questions)) {
            areaData.questions.forEach((q: any) => {
              this.questionsData.push({
                category: category,
                question: q.question || '',
                pass: q.pass || 0,
                score: q.score,
                answer: q.answer,
                options: q.options,
                evidence: q.evidence || ''
              });
            });
          }
        });
      }
    }
    
    // Update chart data - use area_chart_data from result if available
    if (result.area_chart_data && result.area_chart_data.length > 0) {
      this.areaChartData = result.area_chart_data;
    } else {
      this.updateAreaChart();
    }
    
    // Set multi-page properties (discovered pages)
    this.multiPageBaseDomain = baseDomain;
    this.multiPageDiscoveredPages = result.discovered_pages || [];
    this.multiPageResults = {};
    // Get summary_discovered_pages_metrics (at root level now)
    this.summaryDiscoveredPagesMetrics = result.summary_discovered_pages_metrics || {};
    console.log('🔧 _processFlattenedResult: summaryDiscoveredPagesMetrics', this.summaryDiscoveredPagesMetrics);
    
    // Set result flags for HTML conditions
    this.singlePageResult = { base_domain: baseDomain };
    this.multiPageResult = { base_domain: baseDomain, discovered_pages: this.discoveredPages };
    this.auditResult = result;
    
    console.log('✅ _processFlattenedResult: Completed', {
      compositeScore: this.compositeScore,
      auditAreasCount: Object.keys(this.auditAreas).length,
      discoveredPagesCount: this.discoveredPages.length,
      discoveredPages: this.discoveredPages,
      multiPageDiscoveredPagesCount: this.multiPageDiscoveredPages.length,
      multiPageDiscoveredPages: this.multiPageDiscoveredPages,
      discoveredPagesMetricsKeys: Object.keys(this.discoveredPagesMetrics)
    });
    
    this.cdr.detectChanges();
  }
  
  /**
   * Process unified format (works for both single and multi-page) - OLD METHOD
   */
  private _processUnifiedFormat(result: any): void {
    this.baseDomain = result.base_domain || '';
    this.discoveredPages = result.discovered_pages || [];
    this.pageResults = result.results || {};
    this.summaryMetrics = result.summary_metrics || null;
    
    // Determine if multi-page (more than 1 page)
    this.isMultiPageAudit = this.discoveredPages.length > 1;
    
    // Use summary metrics for the 4 cards
    if (this.summaryMetrics) {
      this.auditMetrics = {
        ai_readiness: this.summaryMetrics.ai_readiness,
        static_content: this.summaryMetrics.static_content,
        page_speed: this.summaryMetrics.page_speed,
        structured_data: this.summaryMetrics.structured_data
      };
    }
    
    // Calculate composite score from all pages (works for 1 page or multiple)
    const scores = Object.values(this.pageResults)
      .map((r: any) => r.composite_score || 0)
      .filter((s: number) => s > 0);
    this.compositeScore = scores.length > 0
      ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
      : 0;
    
    // Get audit areas from first page (for area chart and categories table)
    const firstPageResult = Object.values(this.pageResults)[0];
    if (firstPageResult && firstPageResult.audit_areas) {
      this.auditAreas = {};
      
      // Extract area scores
      Object.keys(firstPageResult.audit_areas).forEach(category => {
        const areaData = firstPageResult.audit_areas[category];
        this.auditAreas[category] = {
          current: areaData.current_score || 0,
          potential: areaData.potential_score || 100,
          total_questions: areaData.total_questions || 0,
          passed_questions: areaData.passed_questions || 0,
          questions: areaData.questions || []
        };
      });

      // Collect all questions
      this.questionsData = [];
      Object.keys(firstPageResult.audit_areas).forEach(category => {
        const areaData = firstPageResult.audit_areas[category];
        if (areaData.questions && Array.isArray(areaData.questions)) {
          areaData.questions.forEach((q: any) => {
            this.questionsData.push({
              category: category,
              question: q.question || '',
              pass: q.pass || 0,
              score: q.score,
              answer: q.answer,
              options: q.options,
              evidence: q.evidence || ''
            });
          });
        }
      });
    }

    // Update chart data (always works, uses auditAreas)
    this.updateAreaChart();
  }
  
  /**
   * Process old single-page format (backward compatibility)
   */
  private _processOldSinglePageFormat(actualData: any): void {
      this.isMultiPageAudit = false;
      this.baseDomain = '';
      this.discoveredPages = [];
      this.pageResults = {};
      this.summaryMetrics = null;
      
      // Get composite score
      this.compositeScore = actualData.composite_score || 0;

      // Extract high-level AI audit metrics if present
      this.auditMetrics = actualData.audit_metrics || null;

      // Process audit areas (dynamic categories from CSV)
      if (actualData.audit_areas) {
        this.auditAreas = {};
        
        // Extract area scores
        Object.keys(actualData.audit_areas).forEach(category => {
          const areaData = actualData.audit_areas[category];
          this.auditAreas[category] = {
            current: areaData.current_score || 0,
            potential: areaData.potential_score || 100,
            total_questions: areaData.total_questions || 0,
            passed_questions: areaData.passed_questions || 0,
            questions: areaData.questions || []
          };
        });

        // Collect all questions
        this.questionsData = [];
        Object.keys(actualData.audit_areas).forEach(category => {
          const areaData = actualData.audit_areas[category];
          if (areaData.questions && Array.isArray(areaData.questions)) {
            areaData.questions.forEach((q: any) => {
              this.questionsData.push({
                category: category,
                question: q.question || '',
                pass: q.pass || 0,
                score: q.score,
                answer: q.answer,
                options: q.options,
                evidence: q.evidence || ''
              });
            });
          }
        });
      }

      // Update chart data
      this.updateAreaChart();
  }

  /**
   * Update area chart data for area-chart component
   * Format: [{name: "Current/Potential", series: [{name: "Category", value: score}]}]
   */
  updateAreaChart(): void {
    const categories = Object.keys(this.auditAreas);
    
    const currentSeries = categories.map(category => ({
      name: category,
      value: this.auditAreas[category].current
    }));

    const potentialSeries = categories.map(category => ({
      name: category,
      value: this.auditAreas[category].potential
    }));

    this.areaChartData = [
      {
        name: 'Current',
        series: currentSeries
      },
      {
        name: 'Potential',
        series: potentialSeries
      }
    ];
  }

  /**
   * Calculate average score across all discovered pages for a category
   */
  getAverageDiscoveredPageScore(category: string): number {
    const pages = this.getDiscoveredPagesWithMetrics();
    if (!pages || pages.length === 0) {
      // Fallback to single page audit score
      return this.auditAreas[category]?.current || 0;
    }
    
    let totalScore = 0;
    let pageCount = 0;
    
    pages.forEach(pageData => {
      if (!pageData || !pageData.metrics) return;
      
      let score = 0;
      
      // Try exact category name match first
      if (pageData.metrics.category_metrics && pageData.metrics.category_metrics[category]) {
        score = pageData.metrics.category_metrics[category].score || 0;
      } else {
        // Try to find category by checking all available categories in category_metrics
        if (pageData.metrics.category_metrics) {
          const availableCategories = Object.keys(pageData.metrics.category_metrics);
          // Try case-insensitive match
          const matchedCategory = availableCategories.find(cat => 
            cat.toLowerCase() === category.toLowerCase() ||
            cat.toLowerCase().replace(/\s+/g, '') === category.toLowerCase().replace(/\s+/g, '')
          );
          if (matchedCategory) {
            score = pageData.metrics.category_metrics[matchedCategory].score || 0;
          } else {
            // Fallback to old format
            score = this.getPageCategoryScore(pageData, category);
          }
        } else {
          // Fallback to old format
          score = this.getPageCategoryScore(pageData, category);
        }
      }
      
      if (score !== null && score !== undefined) { // Include 0 scores
        totalScore += score;
        pageCount++;
      }
    });
    
    if (pageCount === 0) {
      return this.auditAreas[category]?.current || 0;
    }
    
    return Math.round((totalScore / pageCount) * 10) / 10; // Round to 1 decimal
  }

  /**
   * Get average passed questions count across all discovered pages for a category
   */
  getAverageDiscoveredPagePassed(category: string): { passed: number; total: number } {
    const pages = this.getDiscoveredPagesWithMetrics();
    if (!pages || pages.length === 0) {
      const questions = this.getQuestionsByCategory(category);
      return {
        passed: this.getPassedCount(questions),
        total: this.getTotalCount(questions)
      };
    }
    
    let totalPassed = 0;
    let totalQuestions = 0;
    let pageCount = 0;
    
    pages.forEach(pageData => {
      const categoryData = this.getPageCategoryData(pageData, category);
      if (categoryData && categoryData.description) {
        // Parse "X of Y questions passed" from description
        const match = categoryData.description.match(/(\d+)\s+of\s+(\d+)/);
        if (match) {
          totalPassed += parseInt(match[1], 10);
          totalQuestions += parseInt(match[2], 10);
          pageCount++;
        }
      }
    });
    
    if (pageCount === 0) {
      const questions = this.getQuestionsByCategory(category);
      return {
        passed: this.getPassedCount(questions),
        total: this.getTotalCount(questions)
      };
    }
    
    // Return average (rounded)
    return {
      passed: Math.round(totalPassed / pageCount),
      total: Math.round(totalQuestions / pageCount)
    };
  }

  /**
   * Get current score for a category (average of discovered pages if available, otherwise single page)
   */
  getCategoryCurrentScore(category: string): number {
    const pages = this.getDiscoveredPagesWithMetrics();
    if (pages && pages.length > 0) {
      return this.getAverageDiscoveredPageScore(category);
    }
    return this.auditAreas[category]?.current || 0;
  }

  /**
   * Get passed questions for a category (average of discovered pages if available, otherwise single page)
   */
  getCategoryPassedQuestions(category: string): { passed: number; total: number } {
    const pages = this.getDiscoveredPagesWithMetrics();
    if (pages && pages.length > 0) {
      return this.getAverageDiscoveredPagePassed(category);
    }
    const questions = this.getQuestionsByCategory(category);
    return {
      passed: this.getPassedCount(questions),
      total: this.getTotalCount(questions)
    };
  }

  /**
   * Get all unique categories from audit areas
   */
  getCategories(): string[] {
    return Object.keys(this.auditAreas);
  }
  
  /**
   * Get categories for single-page view
   */
  getSinglePageCategories(): string[] {
    return Object.keys(this.singlePageAuditAreas);
  }
  
  /**
   * Get categories for multi-page view
   */
  getMultiPageCategories(): string[] {
    return Object.keys(this.multiPageAuditAreas);
  }

  /**
   * Get questions for a specific category
   */
  getQuestionsByCategory(category: string): Array<{
    question: string;
    pass: number;
    score?: number;
    answer?: string;
    options?: string;
    evidence: string;
  }> {
    return this.questionsData
      .filter(q => q.category === category)
      .map(q => ({
        question: q.question,
        pass: q.pass,
        score: q.score,
        answer: q.answer,
        options: q.options,
        evidence: q.evidence
      }));
  }
  
  /**
   * Get questions for single-page view by category
   */
  getSinglePageQuestionsByCategory(category: string): Array<{
    question: string;
    pass: number;
    evidence: string;
  }> {
    return this.singlePageQuestionsData
      .filter(q => q.category === category)
      .map(q => ({
        question: q.question,
        pass: q.pass,
        evidence: q.evidence
      }));
  }
  
  /**
   * Get questions for multi-page view by category
   */
  getMultiPageQuestionsByCategory(category: string): Array<{
    question: string;
    pass: number;
    evidence: string;
  }> {
    return this.multiPageQuestionsData
      .filter(q => q.category === category)
      .map(q => ({
        question: q.question,
        pass: q.pass,
        evidence: q.evidence
      }));
  }

  /**
   * Get only failed questions (pass === 0)
   */
  getFailedQuestions(): Array<{
    category: string;
    question: string;
    pass: number;
    evidence: string;
  }> {
    return this.questionsData.filter(q => q.pass === 0);
  }

  /**
   * Check if score passes (score > 80)
   */
  isPass(score: number): boolean {
    return score > 80;
  }

  /**
   * Get score color based on value (0-100 scale)
   */
  getScoreColor(score: number): string {
    if (score > 80) return '#4caf50'; // Green (Pass)
    if (score >= 60) return '#ff9800'; // Orange
    if (score >= 40) return '#ff5722'; // Red-Orange
    return '#f44336'; // Red (Fail)
  }

  /**
   * Get progress bar gradient color based on percentage score
   * (used for summary cards in AI Audit tab)
   */
  getProgressColor(score: number): string {
    if (score >= 90) return 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'; // Green gradient
    if (score >= 70) return 'linear-gradient(90deg, #26a69a 0%, #4db6ac 100%)'; // Blue-green gradient
    if (score >= 50) return 'linear-gradient(90deg, #ff9800 0%, #ffb74d 100%)'; // Orange gradient
    return 'linear-gradient(90deg, #f44336 0%, #e57373 100%)'; // Red gradient
  }

  /**
   * Get icon for category (dynamic based on category name)
   */
  getCategoryIcon(category: string): string {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('readiness') || categoryLower.includes('ai')) {
      return 'psychology';
    } else if (categoryLower.includes('content') || categoryLower.includes('static')) {
      return 'description';
    } else if (categoryLower.includes('speed') || categoryLower.includes('performance')) {
      return 'speed';
    } else if (categoryLower.includes('structured') || categoryLower.includes('data')) {
      return 'schema';
    } else if (categoryLower.includes('authority') || categoryLower.includes('eeat')) {
      return 'verified';
    } else if (categoryLower.includes('robots')) {
      return 'security';
    } else if (categoryLower.includes('llms')) {
      return 'article';
    } else {
      return 'category';
    }
  }

  /**
   * Get AI audit summary metrics for template
   */
  getAuditMetrics() {
    return this.auditMetrics;
  }
  
  /**
   * Get single-page audit metrics
   */
  getSinglePageAuditMetrics() {
    return this.singlePageAuditMetrics;
  }
  
  /**
   * Get multi-page audit metrics
   */
  getMultiPageAuditMetrics() {
    return this.multiPageAuditMetrics;
  }

  /**
   * Get summary metrics for multi-page audit (includes robots.txt, llms.txt)
   * Returns merged metrics: prefers summary_discovered_pages_metrics for aggregated scores,
   * falls back to summary_metrics for single-page scores
   */
  getSummaryMetrics() {
    // Merge summary_metrics with summary_discovered_pages_metrics
    // Prefer aggregated metrics from discovered pages if available
    const discoveredMetrics = this.summaryDiscoveredPagesMetrics || {};
    const baseMetrics = this.summaryMetrics || {};
    
    return {
      ai_readiness: discoveredMetrics.ai_readiness || baseMetrics.ai_readiness,
      static_content: discoveredMetrics.static_content || baseMetrics.static_content,
      page_speed: discoveredMetrics.page_speed || baseMetrics.page_speed,
      structured_data: discoveredMetrics.structured_data || baseMetrics.structured_data,
      robots_txt: baseMetrics.robots_txt || discoveredMetrics.robots_txt,
      llms_txt: baseMetrics.llms_txt || discoveredMetrics.llms_txt
    };
  }
  
  /**
   * Get single-page summary metrics
   */
  getSinglePageSummaryMetrics() {
    return this.singlePageSummaryMetrics;
  }
  
  /**
   * Get multi-page summary metrics
   */
  getMultiPageSummaryMetrics() {
    return this.multiPageSummaryMetrics;
  }

  /**
   * Check if multi-page audit mode
   * Simple check: more than 1 page = multi-page
   */
  getIsMultiPageAudit(): boolean {
    return this.discoveredPages.length > 1;
  }

  /**
   * Get base domain for multi-page audit
   */
  getBaseDomain(): string {
    return this.baseDomain;
  }

  /**
   * Get discovered pages list
   */
  getDiscoveredPages(): string[] {
    return this.discoveredPages;
  }

  /**
   * Get page result for a specific URL
   */
  getPageResult(url: string): any {
    return this.pageResults[url] || null;
  }
  
  /**
   * Get single-page result for a specific URL
   */
  getSinglePageResult(url: string): any {
    return this.singlePageResults[url] || null;
  }
  
  /**
   * Get multi-page result for a specific URL
   */
  getMultiPageResult(url: string): any {
    return this.multiPageResults[url] || null;
  }

  /**
   * Get page metric value (for table display)
   */
  getPageMetric(url: string, metric: 'ai_readiness' | 'static_content' | 'page_speed' | 'structured_data'): number {
    const pageResult = this.getPageResult(url);
    if (!pageResult || !pageResult.audit_metrics) return 0;
    return pageResult.audit_metrics[metric]?.score || 0;
  }
  
  /**
   * Get single-page metric value
   */
  getSinglePageMetric(url: string, metric: 'ai_readiness' | 'static_content' | 'page_speed' | 'structured_data'): number {
    const pageResult = this.getSinglePageResult(url);
    if (!pageResult || !pageResult.audit_metrics) return 0;
    return pageResult.audit_metrics[metric]?.score || 0;
  }
  
  /**
   * Get multi-page metric value
   */
  getMultiPageMetric(url: string, metric: 'ai_readiness' | 'static_content' | 'page_speed' | 'structured_data'): number {
    const pageResult = this.getMultiPageResult(url);
    if (!pageResult || !pageResult.audit_metrics) return 0;
    return pageResult.audit_metrics[metric]?.score || 0;
  }
  
  /**
   * Check if both results are available
   */
  hasBothResults(): boolean {
    return !!(this.singlePageResult && this.multiPageResult);
  }
  
  /**
   * Get single-page base domain
   */
  getSinglePageBaseDomain(): string {
    return this.singlePageBaseDomain;
  }
  
  /**
   * Get multi-page base domain
   */
  getMultiPageBaseDomain(): string {
    return this.multiPageBaseDomain;
  }
  
  /**
   * Get single-page discovered pages
   */
  getSinglePageDiscoveredPages(): string[] {
    return this.singlePageDiscoveredPages;
  }
  
  /**
   * Get multi-page discovered pages
   */
  getMultiPageDiscoveredPages(): string[] {
    // Use discoveredPages (new format) if available, otherwise fall back to multiPageDiscoveredPages (old format)
    return this.discoveredPages.length > 0 ? this.discoveredPages : this.multiPageDiscoveredPages;
  }
  
  /**
   * Get metrics for a specific discovered page
   */
  getDiscoveredPageMetrics(pageUrl: string): any {
    return this.discoveredPagesMetrics[pageUrl] || null;
  }
  
  /**
   * Get all discovered pages with their metrics
   */
  getDiscoveredPagesWithMetrics(): Array<{ url: string; metrics: any; title?: string }> {
    // Use discoveredPages (new format) if available, otherwise fall back to multiPageDiscoveredPages (old format)
    const pagesToUse = this.discoveredPages.length > 0 ? this.discoveredPages : this.multiPageDiscoveredPages;
    
    return pagesToUse.map(url => {
      const pageMetrics = this.discoveredPagesMetrics[url];
      // Structure: pageMetrics has category_metrics at root level, or audit_metrics for old format
      const metrics = pageMetrics?.category_metrics 
        ? { category_metrics: pageMetrics.category_metrics }
        : (pageMetrics?.audit_metrics || null);
      
      return {
        url,
        metrics: metrics,
        title: pageMetrics?.title || ''
      };
    });
  }
  
  /**
   * Get summary_discovered_pages_metrics (robots.txt/llms.txt)
   */
  getSummaryDiscoveredPagesMetrics(): any {
    return this.summaryDiscoveredPagesMetrics || {};
  }
  
  /**
   * Math helper for template
   */
  Math = Math;

  /**
   * Toggle page details expansion
   */
  togglePageDetails(url: string): void {
    if (this.expandedPages.has(url)) {
      this.expandedPages.delete(url);
    } else {
      this.expandedPages.add(url);
    }
  }

  /**
   * Check if page details are expanded
   */
  isPageExpanded(url: string): boolean {
    return this.expandedPages.has(url);
  }
  
  /**
   * Toggle category expansion for questions
   */
  toggleCategoryExpansion(category: string): void {
    if (this.expandedCategories.has(category)) {
      this.expandedCategories.delete(category);
    } else {
      this.expandedCategories.add(category);
    }
  }
  
  /**
   * Check if category is expanded
   */
  isCategoryExpanded(category: string): boolean {
    return this.expandedCategories.has(category);
  }

  /**
   * Get status icon for robots.txt/llms.txt
   */
  getStatusIcon(status: string): string {
    return status === 'good' ? 'check_circle' : 'cancel';
  }

  /**
   * Get audit area keys from page result
   */
  getAuditAreaKeys(auditAreas: any): string[] {
    if (!auditAreas) return [];
    return Object.keys(auditAreas);
  }

  /**
   * Round number (helper for template)
   */
  round(value: number): number {
    return Math.round(value);
  }

  /**
   * Get brand from audit result
   */
  getBrand(): string {
    if (!this.auditResult) return '';
    const actualData = this.auditResult.structured_data || this.auditResult;
    return actualData.brand || '';
  }

  /**
   * Get URL from audit result
   */
  getUrl(): string {
    if (!this.auditResult) return '';
    const actualData = this.auditResult.structured_data || this.auditResult;
    return actualData.url || '';
  }

  /**
   * Get passed count for questions array
   */
  getPassedCount(questions: Array<{ question: string; pass: number; evidence: string }>): number {
    if (!questions) return 0;
    return questions.filter(q => q.pass === 1).length;
  }

  /**
   * Get total count for questions array
   */
  getTotalCount(questions: Array<{ question: string; pass: number; evidence: string }>): number {
    if (!questions) return 0;
    return questions.length;
  }
  
  /**
   * Re-run AI audit using task-based API with status polling
   */
  reAudit(): void {
    // Get project and filter data
    const projectData = this.genericTemplateCommonService.getCurrentProjectData();
    const filterData = this.genericTemplateCommonService.getCurrentFilterData();
    const projectId = this.projectId || projectData?.id;
    
    // Get projectdata_id from URL or service if available
    const urlParams = new URLSearchParams(window.location.search);
    const projectdataId = urlParams.get('projectdataId') || undefined;
    
    // Build filters for audit
    const brands = filterData?.selectedEntities?.map(e => e.name) || [];
    const country = filterData?.country || 'Global';
    const category = filterData?.category || 'General';
    
    const filters: any = {
      country: country,
      category: category,
      industry: 'General'
    };
    
    if (brands.length > 0) {
      filters.brands = brands;
      filters.brand = brands[0];
    }
    
    // Extract URL/base_domain from project description
    if (projectData?.description) {
      filters.url = projectData.description;
      // Extract base_domain from description for multi-page audit
      const description = projectData.description.trim();
      console.log('🔍 Extracting base_domain from description:', description);
      
      try {
        // First try to match full URL pattern (https:// or http://)
        const urlPattern = /https?:\/\/[^\s\)\]\>\"\']+/;
        const urlMatches = description.match(urlPattern);
        
        if (urlMatches && urlMatches.length > 0) {
          const url = urlMatches[0].replace(/[.,;:!?)]+$/, '');
          try {
            const urlObj = new URL(url);
            filters.base_domain = `${urlObj.protocol}//${urlObj.host}`;
            console.log('✅ Extracted base_domain from URL:', filters.base_domain);
          } catch (e) {
            console.warn('Failed to parse URL, trying domain pattern:', e);
            // Fall through to domain pattern matching
          }
        }
        
        // If no URL pattern matched, try domain pattern (www.example.com or example.com)
        if (!filters.base_domain) {
          // Improved domain pattern: matches www.example.com, example.com, subdomain.example.com
          const domainPattern = /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+)/;
          const domainMatches = description.match(domainPattern);
          
          if (domainMatches && domainMatches.length > 0) {
            // Extract the domain (group 1) or full match
            const domain = domainMatches[1] || domainMatches[0];
            // Remove protocol if present
            const cleanDomain = domain.replace(/^https?:\/\//, '').trim();
            filters.base_domain = `https://${cleanDomain}`;
            console.log('✅ Extracted base_domain from domain pattern:', filters.base_domain);
          } else {
            console.warn('⚠️ No domain pattern matched in description:', description);
          }
        }
      } catch (e) {
        console.error('Error extracting base_domain from description:', e);
      }
      
      // Log final base_domain value
      console.log('🔍 Final base_domain in filters:', filters.base_domain);
    } else {
      console.warn('⚠️ No project description found');
    }
    
    // Start task-based audit
    this.startTaskBasedAudit({
      project_id: projectId,
      projectdata_id: projectdataId,
      filters: filters
    });
    
    // Show snackbar notification
    this.snackBar.open(
      'AI audit started. Progress will be shown below.',
      'Close',
      { duration: 5000 }
    );
  }

  private transformSourcesData(sources: any[]): any[] {
    return sources.map((source, index) => ({
      title: source.title || source.snippet || `Source ${index + 1}`,
      url: source.url,
      confidence: source.confidence || 0.7,
      snippet: source.snippet || source.title,
      source: source.source || "Tavily",
      category: 'ai_audit',
      id: `source_${index}`
    }));
  }

  getEvidenceSources(): any[] {
    return this.evidenceSources || [];
  }

  /**
   * Check if discovery pages should show "NA" (no site in project description)
   */
  hasDiscoveryPagesNA(): boolean {
    // First check component property (set by _processBrandPagesFormat or _processFlattenedResult)
    if (this.discoveryPagesNA === true) {
      return true;
    }
    
    if (!this.auditResult) return false;
    const actualData = this.auditResult.structured_data || this.auditResult;
    
    // Check for new brand/pages format
    if (actualData.pages?.discovery_pages_na === true) {
      return true;
    }
    
    // Check for discovery_pages_na flag (at root level in old flattened format)
    if (actualData.discovery_pages_na === true) {
      return true;
    }
    
    // Backward compatibility: check in single_page_audit wrapper
    if (actualData.single_page_audit?.discovery_pages_na === true) {
      return true;
    }
    
    // Backward compatibility: check in multi_page_result
    if (actualData.multi_page_result?.discovery_pages_na === true) {
      return true;
    }
    
    // Check if discovered_pages is empty and no format_only flag
    const discoveredPages = actualData.pages?.discovered_pages || 
                           actualData.discovered_pages || 
                           actualData.single_page_audit?.discovered_pages || 
                           actualData.multi_page_result?.discovered_pages || 
                           this.discoveredPages || [];
    if (discoveredPages.length === 0 && 
        !actualData.pages?.discovery_pages_format_only &&
        !actualData.discovery_pages_format_only && 
        !actualData.single_page_audit?.discovery_pages_format_only && 
        !actualData.multi_page_result?.discovery_pages_format_only &&
        !this.discoveryPagesFormatOnly) {
      // Only show NA if we have no pages and it's not format-only
      return true;
    }
    
    return false;
  }

  /**
   * Get category score for a specific page
   */
  getPageCategoryScore(pageData: any, category: string): number {
    if (!pageData || !pageData.metrics) return 0;
    
    // Check for category_metrics (new format from CSV categories)
    if (pageData.metrics.category_metrics && pageData.metrics.category_metrics[category]) {
      return pageData.metrics.category_metrics[category].score || 0;
    }
    
    // Fallback to old format (for backward compatibility)
    const oldMetrics = pageData.metrics;
    if (category === 'AI Readiness' && oldMetrics.ai_readiness) {
      return oldMetrics.ai_readiness.score || 0;
    }
    if (category === 'Static Content' && oldMetrics.static_content) {
      return oldMetrics.static_content.score || 0;
    }
    if (category === 'Page Speed Test' && oldMetrics.page_speed) {
      return oldMetrics.page_speed.score || 0;
    }
    if (category === 'Structured Data' && oldMetrics.structured_data) {
      return oldMetrics.structured_data.score || 0;
    }
    
    return 0;
  }

  /**
   * Get category description for a specific page
   */
  getPageCategoryDescription(pageData: any, category: string): string {
    if (!pageData || !pageData.metrics) return 'N/A';
    
    // Check for category_metrics (new format from CSV categories)
    if (pageData.metrics.category_metrics && pageData.metrics.category_metrics[category]) {
      return pageData.metrics.category_metrics[category].description || 'N/A';
    }
    
    // Fallback to old format (for backward compatibility)
    const oldMetrics = pageData.metrics;
    if (category === 'AI Readiness' && oldMetrics.ai_readiness) {
      return oldMetrics.ai_readiness.description || 'N/A';
    }
    if (category === 'Static Content' && oldMetrics.static_content) {
      return oldMetrics.static_content.description || 'N/A';
    }
    if (category === 'Page Speed Test' && oldMetrics.page_speed) {
      return oldMetrics.page_speed.description || 'N/A';
    }
    if (category === 'Structured Data' && oldMetrics.structured_data) {
      return oldMetrics.structured_data.description || 'N/A';
    }
    
    return 'N/A';
  }

  /**
   * Get full category data for a specific page (includes sub-metrics, breakdown, optimization notes)
   * Uses audit areas/questions from main audit result to build detailed breakdown
   */
  getPageCategoryData(pageData: any, category: string): any {
    if (!pageData || !pageData.metrics) return null;
    
    // Get base category metric data
    let categoryData: any = null;
    
    // Check for category_metrics (new format from CSV categories)
    if (pageData.metrics.category_metrics && pageData.metrics.category_metrics[category]) {
      categoryData = { ...pageData.metrics.category_metrics[category] };
    } else {
      // Fallback to old format (for backward compatibility)
      const oldMetrics = pageData.metrics;
      if (category === 'AI Readiness' && oldMetrics.ai_readiness) {
        categoryData = { ...oldMetrics.ai_readiness };
      } else if (category === 'Static Content' && oldMetrics.static_content) {
        categoryData = { ...oldMetrics.static_content };
      } else if (category === 'Page Speed Test' && oldMetrics.page_speed) {
        categoryData = { ...oldMetrics.page_speed };
      } else if (category === 'Structured Data' && oldMetrics.structured_data) {
        categoryData = { ...oldMetrics.structured_data };
      }
    }
    
    if (!categoryData) return null;
    
    // Use breakdown from discovered page's own evaluation (already provided by backend)
    // Only build breakdown if not already provided by backend
    if (!categoryData.breakdown) {
      // Fallback: Get questions for this category from the main audit result (for backward compatibility)
      const categoryQuestions = this.getQuestionsByCategory(category);
      if (categoryQuestions && categoryQuestions.length > 0) {
        // Build breakdown from questions
        categoryData.breakdown = [];
        
        // Group questions by type/theme for breakdown sections
        const breakdownMap: { [key: string]: Array<{ label: string; value: string | boolean }> } = {};
        
        categoryQuestions.forEach((q: any) => {
          // Extract question type from question text (e.g., "Heading hierarchy", "Semantic HTML")
          const questionText = q.question || '';
          let section = 'General';
          let label = questionText;
          
          // Try to extract section and label from question
          if (questionText.includes(':')) {
            const parts = questionText.split(':');
            section = parts[0].trim();
            label = parts.slice(1).join(':').trim();
          } else {
            // Use first few words as section
            const words = questionText.split(' ');
            if (words.length > 3) {
              section = words.slice(0, 2).join(' ');
              label = questionText;
            }
          }
          
          if (!breakdownMap[section]) {
            breakdownMap[section] = [];
          }
          
          breakdownMap[section].push({
            label: label || questionText,
            value: q.pass === 1 ? 'Yes' : 'No'
          });
        });
        
        // Convert breakdown map to array format
        categoryData.breakdown = Object.keys(breakdownMap).map(section => ({
          section: section,
          items: breakdownMap[section]
        }));
        
        // Build sub-metrics from questions (if not already provided)
        if (!categoryData.sub_metrics && categoryQuestions.length > 0) {
          const passedCount = categoryQuestions.filter((q: any) => q.pass === 1).length;
          const totalCount = categoryQuestions.length;
          const passPercentage = totalCount > 0 ? Math.round((passedCount / totalCount) * 100) : 0;
          
          categoryData.sub_metrics = [
            {
              name: 'Questions Passed',
              value: `${passedCount} of ${totalCount} questions`,
              percentage: passPercentage
            }
          ];
        }
      }
    }
    
    return categoryData;
  }

  /**
   * Load audit data from history API (only called on page load, not after audit completion)
   * After audit completion, data comes directly from status.result
   */
  private loadAuditDataFromHistory(projectdataId: string): void {
    this.loading = true; // Show loading while fetching from history
    this.projectHistoryService.getVersionData(projectdataId).subscribe({
      next: (versionData) => {
        // Update only ai-audit data
        if (versionData.data && versionData.data['ai-audit']) {
          const auditData = versionData.data['ai-audit'];
          let extractedData: any = null;
          
          // Extract structured_data if available
          if (auditData.structured_data) {
            extractedData = auditData.structured_data;
          } else if (auditData.response && typeof auditData.response === 'string' && auditData.response.trim().startsWith('{')) {
            try {
              extractedData = JSON.parse(auditData.response);
            } catch (e) {
              console.warn('Failed to parse audit response JSON:', e);
            }
          } else {
            extractedData = auditData;
          }
          
          if (extractedData) {
            this.auditResult = extractedData;
            this.processAIAuditData(extractedData);
            this.loading = false;
            this.cdr.detectChanges();
            
            // Also update the service so other components can access it
            this.genericTemplateCommonService.updateAIAuditResponse(extractedData);
          } else {
            this.loading = false;
          }
        } else {
          this.loading = false;
        }
      },
      error: (err) => {
        console.error('Error loading audit data from history:', err);
        this.loading = false;
        // Don't show error notification on page load - just silently fail
        // User can still trigger a new audit if needed
      }
    });
  }
}

